'use client';

/**
 * RenderInfoSheet — bottom drawer opened from the angle's overflow menu
 * ("Information" item). Shows how the current render was created — Explore
 * (from scratch) or Refine (refinement, with a tap-target to the parent
 * render) — plus the FULL set of settings used (including defaults).
 *
 * Surfaces:
 * 1. A "Type" row indicating Explore vs Refine.
 * 2. For refines only — a tap-target card that loads the parent render via
 *    `GET /renders/{id}` and either swaps it into the carousel in-place
 *    (same concept_angle) or navigates to the parent's concept page.
 * 3. A "Settings used" table — the complete settings snapshot, with
 *    Style / Materials / Colors rendered via the same reference-thumbnail
 *    visual as the render-settings editor (overlapping circles / "Auto" +
 *    circles / "No refs" / library-style square icons).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import {
  Brain,
  Check,
  ChevronRight,
  ClipboardList,
  Copy,
  Cpu,
  Flag,
  Gauge,
  Globe,
  ImageIcon,
  Landmark,
  Layers,
  Loader2,
  Map,
  Maximize2,
  MessageSquare,
  Palette,
  Pencil,
  RefreshCw,
  Signature,
  Sparkles,
} from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/ui/shadcn/drawer';
import { rendersService } from '@/shared/api/services/renders.service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { EnvironmentSettingEnum } from '@/shared/constants';
import { useStyles } from '@/shared/lib/hooks/useStyles';
import { useMaterials } from '@/shared/lib/hooks/useMaterials';
import { usePalettes } from '@/features/palette';
import { useOutputStyles } from '@/shared/api/hooks/use-output-styles';
import { useClassifiedProjectReferences } from '@/features/project-reference/hooks/use-project-references';
import {
  ReferenceSummary,
  ThumbStrip,
} from '@/features/design/render/ui/scrollable-settings/ReferenceSummary';
import { getMaterialsSubtitle } from '@/features/design/render/ui/settings-content/MaterialsContent';
import type { Render, RenderSettings } from '@/types/render';
import { cn } from '@/shared/lib/cn';

interface RenderInfoSheetProps {
  /** The render whose info we want to inspect. */
  render: Render;
  /** Project id — required to load project references for the Style/Materials/
   *  Colors reference visuals. */
  projectId: string;
  /** Angle id currently focused — enables the in-place swap branch. */
  currentAngleId?: string;
  /**
   * In-place swap closure. Called when the parent sits on `currentAngleId`
   * and the host carousel can render it. Receives parent render id.
   */
  onSwapToParent?: (parentRenderId: string) => void;
  /**
   * Cross-page navigation closure. Called when the parent sits on a
   * different concept_angle. Receives the parent's concept id and angle id.
   */
  onNavigateToParent?: (conceptId: string, angleId: string | undefined) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RenderInfoSheet({
  render,
  projectId,
  currentAngleId,
  onSwapToParent,
  onNavigateToParent,
  open,
  onOpenChange,
}: RenderInfoSheetProps) {
  const t = useTranslations('render.info');
  const tConcept = useTranslations('concept');
  const locale = useLocale();

  const isRefine = render.attributes.is_refine === true;

  const parentRenderId =
    render.attributes.settings?.parent_render_id ??
    render.relationships?.parent_render?.data?.id ??
    null;

  // Parent render fetched on demand. The host concept-detail query already
  // sideloads renders for the *current* concept, so the parent is only
  // missing when it lives in another concept — that's precisely when the
  // network round-trip is required. Skipped entirely for Explore renders.
  const {
    data: parent,
    isLoading: parentLoading,
    isError: parentError,
  } = useQuery<Render>({
    queryKey: cacheKeys.renders.single(parentRenderId as string),
    queryFn: () => rendersService.getRender(parentRenderId as string),
    enabled: open && isRefine && !!parentRenderId,
    staleTime: 5 * 60 * 1000,
  });

  const parentImageUrl =
    parent?.relationships?.file?.meta?.url ?? parent?.attributes.file_url;

  const parentConceptSequenceNumber =
    parent?.relationships?.concept_angle?.meta?.concept_sequence_number;

  const parentTitle = parentLoading
    ? t('parent.loading')
    : parentError || !parent
      ? t('parent.unavailable')
      : parentConceptSequenceNumber !== undefined
        ? tConcept('conceptNumber', { number: parentConceptSequenceNumber })
        : t('parent.label');

  const parentSubtitle = useMemo(() => {
    if (!parent || parentLoading) return null;
    const completedAt = parent.attributes.completed_at;
    if (!completedAt) return null;
    return formatRelativeTime(new Date(completedAt), locale);
  }, [parent, parentLoading, locale]);

  const parentAngleId =
    parent?.relationships?.concept_angle?.data?.id ?? undefined;
  const parentConceptId =
    parent?.relationships?.concept_angle?.meta?.concept_id ?? undefined;

  const handleParentTap = () => {
    if (!parent) return;
    if (
      currentAngleId &&
      parentAngleId === currentAngleId &&
      onSwapToParent
    ) {
      onOpenChange(false);
      // Defer to next tick so the dialog finishes its close transition
      // before the carousel selection mutation lands.
      setTimeout(() => onSwapToParent(parent.id), 0);
      return;
    }
    if (parentConceptId && onNavigateToParent) {
      onOpenChange(false);
      setTimeout(() => onNavigateToParent(parentConceptId, parentAngleId), 0);
    }
  };

  const parentTapDisabled = parentLoading || !parent;

  return (
    // Bottom drawer (not a centered Dialog): the render-info panel is a
    // read-only sheet, so it follows the same iOS-style bottom-sheet pattern as
    // the rest of the app (LLM feedback, job queue) — drag-down / overlay-click
    // / handle to dismiss. The old Dialog rendered top-anchored on mobile
    // (`top-[20%]`) and read as a stray top sheet with no obvious close.
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className={cn(
          'max-h-[90vh]',
          // On wide screens a full-width bottom sheet reads as a stray bar;
          // center it and cap the width (same desktop treatment as
          // DrawerContentFullScreen), keeping the bottom-sheet behavior.
          'sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-[560px] sm:max-w-[calc(100vw-2rem)]'
        )}
      >
        <DrawerHeader>
          <DrawerTitle className="text-sheet-title">{t('title')}</DrawerTitle>
        </DrawerHeader>

        <div className="space-y-6 overflow-y-auto px-4 pb-8">
          {/* Mode section — always visible */}
          <section className="space-y-2">
            <h3 className="text-section-eyebrow text-muted-foreground">
              {t('mode.title')}
            </h3>
            <div className="rounded-md bg-muted flex items-center gap-3 p-3">
              {isRefine ? (
                <RefreshCw className="w-4 h-4 text-muted-foreground" />
              ) : (
                <Sparkles className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-list-item-title">
                {isRefine ? t('mode.refinement') : t('mode.fromScratch')}
              </span>
            </div>
          </section>

          {/* Parent section — refines only */}
          {isRefine && (
            <section className="space-y-2">
              <h3 className="text-section-eyebrow text-muted-foreground">
                {t('parent.title')}
              </h3>
              <button
                type="button"
                onClick={handleParentTap}
                disabled={parentTapDisabled}
                className={cn(
                  'w-full text-start rounded-md bg-muted',
                  'flex items-center gap-3 p-3',
                  'transition-colors hover:bg-muted/80',
                  parentTapDisabled && 'cursor-not-allowed opacity-90'
                )}
              >
                {/* Thumbnail */}
                <div className="w-16 h-12 shrink-0 rounded-sm overflow-hidden bg-background/60 flex items-center justify-center">
                  {parentImageUrl ? (
                    <img
                      src={parentImageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : parentLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-muted-foreground/60" />
                  )}
                </div>

                {/* Title + meta */}
                <div className="flex-1 min-w-0">
                  <p className="text-list-item-title truncate">{parentTitle}</p>
                  {parentSubtitle && (
                    <p className="text-list-item-meta text-muted-foreground truncate">
                      {parentSubtitle}
                    </p>
                  )}
                </div>

                {!parentLoading && parent && (
                  <ChevronRight className="w-3 h-3 text-muted-foreground/60 shrink-0 rtl:-scale-x-100" />
                )}
              </button>
            </section>
          )}

          {/* Settings section — full settings snapshot (incl. defaults). */}
          <section className="space-y-2">
            <h3 className="text-section-eyebrow text-muted-foreground">
              {t('settings.title')}
            </h3>
            <RenderInfoSettingsList
              settings={render.attributes.settings}
              projectId={projectId}
              isRefine={isRefine}
            />
          </section>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface SettingsRow {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** Plain-text value (right-aligned, line-clamped). */
  value?: string;
  /** Rich value (reference thumbnails / library icons). Takes precedence. */
  valueContent?: ReactNode;
  /**
   * Raw text offered for one-tap clipboard copy (brief / design prompt / view
   * prompt). Present only on free-text rows — the value is long enough that
   * manual selection inside a scrolling sheet is painful.
   */
  copyText?: string;
}

/**
 * Renders the full "Settings used" table for a render's immutable settings
 * snapshot. Always surfaces Output style / Model / Effort / Style / Materials
 * / Colors / Environment — including their default values — and appends the
 * prompts / controls rows when present.
 *
 * Style / Materials / Colors reuse the render-settings editor visuals:
 * `ThumbStrip` + `ReferenceSummary` (overlapping circles / "Auto" + circles /
 * "No refs" / library-style square icons). References are sourced from the
 * project via `useClassifiedProjectReferences`, selected ids from the snapshot.
 */
function RenderInfoSettingsList({
  settings,
  projectId,
  isRefine,
}: {
  settings: RenderSettings | undefined;
  projectId: string;
  isRefine: boolean;
}) {
  const tSettings = useTranslations('render.settings');
  const tProject = useTranslations('project');

  const { groupedStyles } = useStyles();
  const { materials } = useMaterials();
  const { palettes } = usePalettes();
  const { data: outputStyles = [] } = useOutputStyles();

  // Project references per category (the "auto = all refs" pool).
  const { data: styleCategoryRefs = [] } = useClassifiedProjectReferences(
    projectId,
    'style',
    { enabled: !!projectId }
  );
  const { data: materialCategoryRefs = [] } = useClassifiedProjectReferences(
    projectId,
    'material',
    { enabled: !!projectId }
  );
  const { data: colorCategoryRefs = [] } = useClassifiedProjectReferences(
    projectId,
    'color',
    { enabled: !!projectId }
  );

  // Rows split into the render-settings editor's two visual groups, in the
  // editor's order: creative inputs (prompt → sketches → style/materials/
  // colors → environment), then engine/output (effort → model → extended
  // thinking → output style → hi-res).
  const { creativeRows, engineRows } = useMemo<{
    creativeRows: SettingsRow[];
    engineRows: SettingsRow[];
  }>(() => {
    if (!settings) return { creativeRows: [], engineRows: [] };
    const result: SettingsRow[] = [];
    const engine: SettingsRow[] = [];

    // 0a. Tasks (project goals) — project-level context snapshotted into the
    // render at create time; shown when present. Mirrors Apple/Android.
    const projectGoals =
      typeof settings.project_goals === 'string'
        ? settings.project_goals.trim()
        : '';
    if (projectGoals) {
      result.push({
        icon: ClipboardList,
        label: tProject('projectGoalsNav'),
        value: projectGoals,
        copyText: projectGoals,
      });
    }

    // 0b. Construction region — rides with the goals as project-level context;
    // shown when present. Mirrors Apple/Android.
    const constructionRegion =
      typeof settings.construction_region === 'string'
        ? settings.construction_region.trim()
        : '';
    if (constructionRegion) {
      result.push({
        icon: Map,
        label: tProject('constructionRegion'),
        value: constructionRegion,
      });
    }

    // 1. Concept prompt — shown only when present.
    const conceptPrompt = settings.prompts?.prompt_concept;
    if (conceptPrompt) {
      result.push({
        icon: MessageSquare,
        label: tSettings('prompts.conceptPrompt'),
        value: conceptPrompt,
        copyText: conceptPrompt,
      });
    }

    // 2. Angle prompt — shown only when present.
    const anglePrompt = settings.prompts?.prompt_angle;
    if (anglePrompt) {
      result.push({
        icon: MessageSquare,
        label: tSettings('prompts.anglePrompt'),
        value: anglePrompt,
        copyText: anglePrompt,
      });
    }

    // 3. Controls (sketches) — shown only when present.
    const controls = settings.controls;
    if (controls && controls.length > 0) {
      result.push({
        icon: Pencil,
        label: tSettings('control.title'),
        value: String(controls.length),
      });
    }

    // 4. Style — library squares when picked, else the references visual.
    const allStyles = [
      ...groupedStyles.contemporary,
      ...groupedStyles.traditional,
    ];
    const selectedStyleImages = (settings.style_ids ?? [])
      .map(
        (id) => allStyles.find((s) => parseInt(s.id) === id)?.attributes.image_url
      )
      .filter((url): url is string => !!url);
    result.push({
      icon: Landmark,
      label: tSettings('style.title'),
      valueContent:
        selectedStyleImages.length > 0 ? (
          <ThumbStrip images={selectedStyleImages} shape="square" invert />
        ) : (
          <ReferenceSummary
            selectedRefImages={refImages(
              settings.style_project_reference_ids,
              styleCategoryRefs
            )}
            allRefImages={allRefImages(styleCategoryRefs)}
            useAutoRefs={settings.use_auto_style_refs ?? true}
            isRefine={isRefine}
            autoLabel={tSettings('style.auto')}
          />
        ),
    });

    // 5. Materials — library selection → names text (materials have no images);
    //    otherwise the references visual.
    const hasSelectedMaterials = (settings.material_ids?.length ?? 0) > 0;
    if (hasSelectedMaterials) {
      result.push({
        icon: Layers,
        label: tSettings('materials.title'),
        value: getMaterialsSubtitle(
          settings.material_ids ?? null,
          materials,
          ''
        ),
      });
    } else {
      result.push({
        icon: Layers,
        label: tSettings('materials.title'),
        valueContent: (
          <ReferenceSummary
            selectedRefImages={refImages(
              settings.material_project_reference_ids,
              materialCategoryRefs
            )}
            allRefImages={allRefImages(materialCategoryRefs)}
            useAutoRefs={settings.use_auto_material_refs ?? true}
            isRefine={isRefine}
            autoLabel={tSettings('materials.autoMode')}
          />
        ),
      });
    }

    // 6. Colors — palette name / "Custom" text; otherwise the references visual.
    const colors = settings.colors ?? null;
    const hasColorRefs =
      (settings.color_project_reference_ids?.length ?? 0) > 0;
    const hasPaletteOrCustom = (colors?.length ?? 0) > 0;
    if (hasPaletteOrCustom && !hasColorRefs) {
      let colorValue: string;
      const isPalette = colors!.some(
        (c) => typeof c === 'string' && c.startsWith('palette:')
      );
      if (isPalette) {
        const paletteId = parseInt(colors![0]!.substring(8));
        colorValue =
          palettes.find((p) => parseInt(p.id) === paletteId)?.attributes.name ??
          tSettings('colors.palette');
      } else {
        colorValue = tSettings('colors.custom');
      }
      result.push({ icon: Palette, label: tSettings('colors.title'), value: colorValue });
    } else {
      result.push({
        icon: Palette,
        label: tSettings('colors.title'),
        valueContent: (
          <ReferenceSummary
            selectedRefImages={refImages(
              settings.color_project_reference_ids,
              colorCategoryRefs
            )}
            allRefImages={allRefImages(colorCategoryRefs)}
            useAutoRefs={settings.use_auto_color_refs ?? true}
            isRefine={isRefine}
            autoLabel={tSettings('colors.auto')}
          />
        ),
      });
    }

    // 7. Environment — always; NO_CHANGE is the default.
    const envSetting =
      (settings.environment_setting as string | undefined) ??
      EnvironmentSettingEnum.NO_CHANGE;
    const envKey = ENVIRONMENT_LABEL_KEYS[envSetting] ?? 'noChange';
    result.push({
      icon: Globe,
      label: tSettings('environment.title'),
      value: tSettings(`environment.${envKey}`),
    });

    // 7b. Ambition — creative input, so it sits with the design rows rather than
    // the engine block. Absent in snapshots taken before the setting existed →
    // BALANCED, which is exactly how those renders were generated (the middle
    // injects nothing into the prompt), so this reports history honestly.
    const ambition = settings.render_ambition ?? 'BALANCED';
    result.push({
      icon: Flag,
      label: tSettings('ambition.title'),
      value: tSettings(`ambition.types.${ambition}.name`),
    });

    // 8. Effort — always. HISTORICAL read: this sheet reports what an already
    // finished render used, so the fallback stays STANDARD even though new renders
    // now default to BRAINSTORM. A settings snapshot with no effort predates the
    // default change, and the server did run it at STANDARD — showing Brainstorm
    // here would misreport history. Mirrors Android RenderInfoSheet / Apple
    // RenderSettingsSummaryView. Do not "align" this with the live paths.
    const effort = settings.render_effort ?? 'STANDARD';
    engine.push({
      icon: Gauge,
      label: tSettings('effort.title'),
      value: tSettings(`effort.types.${effort}.name`),
    });

    // 9. Model — always; AURORA is the default family.
    const modelFamily = settings.render_model_family ?? 'AURORA';
    engine.push({
      icon: Cpu,
      label: tSettings('model.title'),
      value: tSettings(`model.types.${modelFamily}.name`),
    });

    // 10. Extended thinking — On/Off; absent in old snapshots = Off.
    const isExtendedThinking =
      (settings.is_extended_thinking as boolean | undefined) ?? false;
    engine.push({
      icon: Brain,
      label: tSettings('extendedThinking.title'),
      value: isExtendedThinking ? tSettings('on') : tSettings('off'),
    });

    // 11. Output style — mirrors Apple's `outputStyleRow` precedence: output
    //     references → "From reference"; else the resolved style name; else
    //     Photorealistic (no output style selected, no output refs).
    const outputStyleId = settings.output_style_id as number | null | undefined;
    const hasOutputRefs =
      (settings.output_project_reference_ids?.length ?? 0) > 0;
    const outputStyleName =
      outputStyleId != null
        ? outputStyles.find((s) => parseInt(s.id) === outputStyleId)?.attributes
            .name
        : null;
    engine.push({
      icon: Signature,
      label: tSettings('output.title'),
      value: hasOutputRefs
        ? tSettings('output.fromReference')
        : (outputStyleName ?? tSettings('output.photorealistic')),
    });

    // 12. Hi-Res — On/Off; absent in old snapshots = Off.
    const isHires = (settings.is_hires as boolean | undefined) ?? false;
    engine.push({
      icon: Maximize2,
      label: tSettings('hires.title'),
      value: isHires ? tSettings('on') : tSettings('off'),
    });

    return { creativeRows: result, engineRows: engine };
  }, [
    settings,
    isRefine,
    groupedStyles,
    materials,
    palettes,
    outputStyles,
    styleCategoryRefs,
    materialCategoryRefs,
    colorCategoryRefs,
    tSettings,
    tProject,
  ]);

  // Two cards mirroring the render-settings editor's grouping: creative
  // inputs first, then the engine/output rows.
  return (
    <>
      <SettingsRowsCard rows={creativeRows} />
      <SettingsRowsCard rows={engineRows} />
    </>
  );
}

/** One visual group of "Settings used" rows on the shared muted card.
 * `select-text` opts back out of the global app-like selection guard: these
 * rows hold past prompts/goals the user may genuinely want to copy. */
function SettingsRowsCard({ rows }: { rows: SettingsRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="rounded-md bg-muted overflow-hidden select-text">
      {rows.map((row, index) => {
        const Icon = row.icon;
        return (
          <div key={`${row.label}-${index}`}>
            {index > 0 && <div className="ms-3 h-px bg-border" aria-hidden />}
            <div className="flex items-start gap-2 px-3 py-2.5">
              <Icon className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              <span className="text-list-item-subtitle shrink-0">{row.label}</span>
              <span className="ms-auto flex items-center justify-end text-list-item-value text-muted-foreground text-end line-clamp-3 min-w-0">
                {row.valueContent ?? row.value}
              </span>
              {row.copyText && <CopyTextButton text={row.copyText} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * One-tap clipboard copy for a free-text settings row (brief / prompts). The
 * glyph swaps to a check for two seconds instead of raising a toast — the row
 * sits inside a sheet where a toast would be covered by the overlay.
 */
function CopyTextButton({ text }: { text: string }) {
  const tCommon = useTranslations('common');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard denied (insecure context / permission) — the text stays
      // selectable in the row, so there is nothing to report.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={tCommon('copy')}
      title={tCommon('copy')}
      className="shrink-0 mt-0.5 text-muted-foreground transition-colors hover:text-foreground"
    >
      {copied ? (
        <Check className="w-4 h-4" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </button>
  );
}

/** Map selected reference ids → image URLs against a category pool. */
function refImages(
  ids: string[] | undefined,
  pool: Array<{ id: string; attributes: { file_url?: string | null } }>
): string[] {
  return (ids ?? [])
    .map((id) => pool.find((r) => r.id === id)?.attributes.file_url ?? null)
    .filter((url): url is string => !!url);
}

/** All reference image URLs for a category (the "auto = all refs" pool). */
function allRefImages(
  pool: Array<{ attributes: { file_url?: string | null } }>
): string[] {
  return pool
    .map((r) => r.attributes.file_url)
    .filter((url): url is string => !!url);
}

// Environment setting enum value → the localized label key under
// `render.settings.environment.*`. Mirrors `EnvironmentContent` options.
const ENVIRONMENT_LABEL_KEYS: Record<string, string> = {
  [EnvironmentSettingEnum.NO_CHANGE]: 'noChange',
  [EnvironmentSettingEnum.CLEAR_DEBRIS]: 'clearDebris',
  [EnvironmentSettingEnum.SIMPLE_LAWN]: 'simpleLawn',
  [EnvironmentSettingEnum.FULL_LANDSCAPE]: 'fullLandscape',
};

/**
 * Cheap relative time ("2h ago", "вчера") via `Intl.RelativeTimeFormat`.
 * Self-contained so the modal does not pull in a date-fns dependency.
 */
function formatRelativeTime(date: Date, locale: string): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSeconds);

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ];

  for (const [unit, secondsInUnit] of units) {
    if (abs >= secondsInUnit) {
      return rtf.format(Math.round(diffSeconds / secondsInUnit), unit);
    }
  }
  return rtf.format(diffSeconds, 'second');
}
